/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useEffect } from 'react';
import GQL from '@lib/cms/gql';
import { EVENT_ATTRIBUTES } from '@lib/fragments';
import Container from '@layout/container';
import TopicCarousel from '@components/topic-carousel';
import Grid from '@layout/grid';
import FeaturedContent from '@components/featured-content';
import EventCard from '@components/event-card';
import Spinner from '@components/spinner';

import styles from './styles.module.scss';

const { header_container } = styles;

// Generic GraphQL response wrapper
interface GraphQLResponse<T> {
  data?: T;
}

interface EventRecord {
  __typename: string;
  sys: { id: string };
}

interface EventRecordCollection {
  items: EventRecord[];
}

interface CommunityCollection {
  items: Community[];
}

interface MetroCollection {
  items: Metro[];
}

interface Community {
  sys: { id: string };
  title: string;
  linkedFrom: {
    eventRecordCollection: EventRecordCollection;
  };
}

interface Metro {
  sys: { id: string };
  title: string;
  linkedFrom: {
    eventRecordCollection: EventRecordCollection;
  };
}

interface GetEventsResponse {
  eventRecordCollection: EventRecordCollection;
  communityCollection: CommunityCollection;
  metroCollection: MetroCollection;
}

interface FeaturedEventsProps {
  header?: string;
  tags?: string[];
  communityMetro?: CommunityCollection;
  events?: EventRecordCollection;
}

const getFeaturedEvents = async (
  tags?: string[],
  communityMetro?: { sys: { id: string } }[]
): Promise<EventRecord[]> => {
  const normalizedCommunityMetro = communityMetro?.map((item) => item.sys.id);

  try {
    const query = `
    ${EVENT_ATTRIBUTES}
      query GetEvents($tags: [String!], $communityMetro: [String!]) {
        eventRecordCollection(
          where: {
            contentfulMetadata: {
              tags: {
                id_contains_some: $tags
              }
            }
            }
            limit: 16 order: sys_publishedAt_DESC
        ) {
          items {
         ...EventRecord
          }
        }
        communityCollection(
          where: {
            sys: {
              id_in: $communityMetro
            }
          }
            limit: 16
        ) {
          items {
            linkedFrom {
              eventRecordCollection(limit: 100 order: sys_publishedAt_DESC) {
                items {
                 ...EventRecord
                }
              }
            }
          }
        }
        metroCollection(
          where: {
            sys: {
              id_in: $communityMetro
            }

          }
            limit: 16
        ) {
          items {
            linkedFrom {
              eventRecordCollection(limit: 100 order: sys_publishedAt_DESC) {
                items {
                 ...EventRecord
                }
              }
            }
          }
        }
      }
    `;

    const data = (await GQL.fetch(
      query,
      { tags, communityMetro: normalizedCommunityMetro },
      false,
      process.env.NEXT_PUBLIC_CONTENTFUL_ENV
    )) as GraphQLResponse<GetEventsResponse>;

    const intersectBySysId = (arr1: EventRecord[], arr2: EventRecord[]) => {
      const ids2 = new Set(arr2.map((item) => item?.sys?.id));
      return arr1.filter((item) => ids2.has(item?.sys?.id));
    };

    const eventsByTags = data?.data?.eventRecordCollection?.items || [];

    const eventsByCommunity =
      data?.data?.communityCollection?.items?.flatMap(
        (community) => community.linkedFrom.eventRecordCollection.items
      ) || [];
    const eventsByMetro = data?.data?.metroCollection?.items?.flatMap(
      (metro) => metro.linkedFrom.eventRecordCollection.items
    );

    const eventsByArea = [...eventsByCommunity, ...eventsByMetro];

    // Only grab the events that are true in both arrays if both arrays are not empty
    if (eventsByTags.length > 0 && eventsByArea.length > 0) {
      return intersectBySysId(eventsByTags, eventsByArea);
    }

    if (eventsByTags.length > 0) {
      return eventsByTags;
    }

    if (eventsByArea.length > 0) {
      return eventsByArea;
    }

    return data?.data?.eventRecordCollection?.items || [];
  } catch (error) {
    console.error('Error fetching events:', error);
    return [];
  }
};

const FeaturedEvents = ({ header, tags, communityMetro, events }: FeaturedEventsProps) => {
  const [eventRecords, setEventRecords] = useState<EventRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      if (events?.items?.length > 0) {
        setEventRecords(events.items);
        setLoading(false);
      } else if (tags?.length > 0 || communityMetro?.items?.length > 0) {
        const fetchedEvents = await getFeaturedEvents(tags, communityMetro?.items);
        setEventRecords(fetchedEvents);
        setLoading(false);
      } else {
        setLoading(false);
      }
    };

    void fetchEvents();
  }, []);

  const eventCardsDisplay = () => {
    if (eventRecords?.length === 1) {
      return (
        <FeaturedContent
          {...eventRecords[0]}
          forceWhiteBackground
          removeHorizontalPadding
        ></FeaturedContent>
      );
    }

    if (eventRecords?.length <= 3) {
      return (
        <Grid layout="third">
          {loading && <Spinner />}
          {!loading &&
            eventRecords.map((event, idx) => <EventCard key={idx} {...event} theme="basic" />)}
        </Grid>
      );
    }

    if (eventRecords?.length > 3) {
      return <TopicCarousel slidesCollection={{ cards: eventRecords }} theme="events" />;
    }

    return null;
  };

  if (!loading && eventRecords?.length === 0) {
    return null;
  }

  return (
    <Container layout="single">
      {header && (
        <div className={header_container}>
          <h2>{header}</h2>
        </div>
      )}
      {eventCardsDisplay()}
    </Container>
  );
};

export default FeaturedEvents;
